import mila from 'markdown-it-link-attributes';
import mentionPlugin from './markdownIt/link';
import MarkdownIt from 'markdown-it';

const setImageHeight = inlineToken => {
  const imgSrc = inlineToken.attrGet('src');
  if (!imgSrc) return;
  const url = new URL(imgSrc);
  const height = url.searchParams.get('cw_image_height');
  if (!height) return;
  inlineToken.attrSet('style', `height: ${height};`);
};

const processInlineToken = blockToken => {
  blockToken.children.forEach(inlineToken => {
    if (inlineToken.type === 'image') {
      setImageHeight(inlineToken);
    }
  });
};

const imgResizeManager = md => {
  // Custom rule for image resize in markdown
  // If the image url has a query param cw_image_height, then add a style attribute to the image
  md.core.ruler.after('inline', 'add-image-height', state => {
    state.tokens.forEach(blockToken => {
      if (blockToken.type === 'inline') {
        processInlineToken(blockToken);
      }
    });
  });
};

const createMarkdownInstance = (linkify = true) => {
  return MarkdownIt({
    html: false,
    xhtmlOut: true,
    breaks: true,
    langPrefix: 'language-',
    linkify,
    typographer: true,
    quotes: '\u201c\u201d\u2018\u2019',
    maxNesting: 20,
  })
    .disable(['lheading'])
    .use(mentionPlugin)
    .use(imgResizeManager)
    .use(mila, {
      attrs: {
        class: 'link',
        rel: 'noreferrer noopener nofollow',
        target: '_blank',
      },
    });
};

const TWITTER_USERNAME_REGEX = /(^|[^@\w])@(\w{1,15})\b/g;
const TWITTER_USERNAME_REPLACEMENT = '$1[@$2](http://twitter.com/$2)';
const TWITTER_HASH_REGEX = /(^|\s)#(\w+)/g;
const TWITTER_HASH_REPLACEMENT = '$1[#$2](https://twitter.com/hashtag/$2)';

// WhatsApp uses a single asterisk pair (`*text*`) as its native bold syntax,
// while CommonMark (used by markdown-it) treats that as italic (`**text**`
// is bold in CommonMark). This converts WhatsApp-style bold into CommonMark
// bold before rendering, so `*text*` shows up bold like it does on WhatsApp.
//
// - `(?<!\*)` / `(?!\*)` around each delimiter make sure we skip pairs that
//   are already part of `**text**` (double asterisk bold), so those are
//   left untouched instead of becoming `****text****`.
// - Requiring the wrapped content to start/end with a non-space character
//   (`\S`) means a list marker like `* item` (asterisk followed by a space)
//   never gets treated as an opening delimiter.
// - `[^*\n]` keeps the match within a single line and requires a matching
//   closing asterisk, so unpaired asterisks (e.g. `3*4=12`) are left alone.
const WHATSAPP_BOLD_REGEX = /(?<!\*)\*(?!\*)(\S(?:[^*\n]*\S)?)\*(?!\*)/g;
const WHATSAPP_BOLD_REPLACEMENT = '**$1**';

const convertWhatsAppBoldToMarkdown = message =>
  message.replace(WHATSAPP_BOLD_REGEX, WHATSAPP_BOLD_REPLACEMENT);

class MessageFormatter {
  constructor(
    message,
    isATweet = false,
    isAPrivateNote = false,
    linkify = true
  ) {
    this.message = message || '';
    this.isAPrivateNote = isAPrivateNote;
    this.isATweet = isATweet;
    this.linkify = linkify;
    this.md = createMarkdownInstance(linkify);
  }

  formatMessage() {
    let updatedMessage = this.message;
    if (this.isATweet && !this.isAPrivateNote) {
      updatedMessage = updatedMessage.replace(
        TWITTER_USERNAME_REGEX,
        TWITTER_USERNAME_REPLACEMENT
      );
      updatedMessage = updatedMessage.replace(
        TWITTER_HASH_REGEX,
        TWITTER_HASH_REPLACEMENT
      );
    }
    updatedMessage = convertWhatsAppBoldToMarkdown(updatedMessage);
    return this.md.render(updatedMessage);
  }

  get formattedMessage() {
    return this.formatMessage();
  }

  get plainText() {
    const strippedOutHtml = new DOMParser().parseFromString(
      this.formattedMessage,
      'text/html'
    );
    return strippedOutHtml.body.textContent || '';
  }
}

export default MessageFormatter;
